import {
	ArrowLeft,
	Keyboard,
	Loader2,
	MessageSquare,
	Monitor,
	Moon,
	Paintbrush,
	Play,
	RotateCcw,
	Sun,
	User,
	Volume2,
	X,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useCallback, useEffect, useState } from "react";
import { ulid } from "ulid";
import { useStore } from "zustand";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { usePresenceLabel } from "@/hooks/use-presence";
import { playNotificationSound } from "@/lib/notification-sound";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import {
	DEFAULT_KEYBINDS,
	formatKeybind,
	KEYBIND_LABELS,
	type Keybind,
	type KeybindAction,
	uiStore,
} from "@/lib/stores/ui-store";
import { cn } from "@/lib/utils";
import { ws } from "@/lib/websocket-wrapper";
import { ProfileImageUpload } from "./profile-image-upload";
import { SignedInHeading } from "./signed-in-heading";

type SettingsSection = "profile" | "chat" | "appearance" | "keybinds";

const sections: {
	id: SettingsSection;
	label: string;
	icon: React.ElementType;
	category: string;
}[] = [
	{ id: "profile", label: "Profile", icon: User, category: "User Settings" },
	{ id: "chat", label: "Chat", icon: MessageSquare, category: "App Settings" },
	{
		id: "appearance",
		label: "Appearance",
		icon: Paintbrush,
		category: "App Settings",
	},
	{
		id: "keybinds",
		label: "Keybinds",
		icon: Keyboard,
		category: "App Settings",
	},
];

type SettingsModalProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
};

export function SettingsModal({ open, onOpenChange }: SettingsModalProps) {
	const [activeSection, setActiveSection] = useState<SettingsSection | null>(
		null
	);

	const handleBack = () => setActiveSection(null);
	const handleSelect = (section: SettingsSection) => setActiveSection(section);

	return (
		<Dialog onOpenChange={onOpenChange} open={open}>
			<DialogContent
				className="flex h-[85vh] max-h-[720px] w-[calc(100%-2rem)] max-w-[900px] flex-col gap-0 overflow-hidden p-0 sm:max-w-[900px] md:h-[80vh] md:flex-row"
				showCloseButton={false}
			>
				<DialogTitle className="sr-only">Settings</DialogTitle>

				{/* Mobile shows either the sidebar or the content, not both. */}
				<div
					className={cn(
						"flex flex-1 flex-col overflow-hidden md:hidden",
						activeSection && "hidden"
					)}
				>
					<div className="flex items-center justify-between border-b px-4 py-3">
						<h2 className="font-semibold text-lg">Settings</h2>
						<Button
							className="size-8"
							onClick={() => onOpenChange(false)}
							size="icon"
							variant="ghost"
						>
							<X className="size-4" />
						</Button>
					</div>
					<div className="flex-1 overflow-y-auto p-2">
						<SettingsSidebar
							activeSection={activeSection}
							onSelect={handleSelect}
						/>
					</div>
				</div>

				<div
					className={cn(
						"flex flex-1 flex-col overflow-hidden md:hidden",
						!activeSection && "hidden"
					)}
				>
					<div className="flex items-center gap-2 border-b px-4 py-3">
						<Button
							className="size-8"
							onClick={handleBack}
							size="icon"
							variant="ghost"
						>
							<ArrowLeft className="size-4" />
						</Button>
						<h2 className="font-semibold text-lg">
							{activeSection &&
								sections.find((s) => s.id === activeSection)?.label}
						</h2>
					</div>
					<ScrollArea className="flex-1">
						<div className="p-4">
							{activeSection === "profile" && <ProfileSettings />}
							{activeSection === "chat" && <ChatSettings />}
							{activeSection === "appearance" && <AppearanceSettings />}
							{activeSection === "keybinds" && <KeybindSettings />}
						</div>
					</ScrollArea>
				</div>

				{/* Desktop lays the two out side by side. */}
				<div className="hidden w-[220px] shrink-0 flex-col overflow-hidden border-r bg-muted/30 md:flex">
					<div className="flex-1 overflow-y-auto p-2">
						<SettingsSidebar
							activeSection={activeSection}
							onSelect={handleSelect}
						/>
					</div>
				</div>

				<div className="hidden flex-1 flex-col overflow-hidden md:flex">
					<div className="flex items-center justify-between border-b px-6 py-4">
						<h2 className="font-semibold text-lg">
							{activeSection
								? sections.find((s) => s.id === activeSection)?.label
								: sections[0].label}
						</h2>
						<Button
							className="size-8"
							onClick={() => onOpenChange(false)}
							size="icon"
							variant="ghost"
						>
							<X className="size-4" />
						</Button>
					</div>
					<ScrollArea className="flex-1">
						<div className="p-6">
							{(activeSection || "profile") === "profile" && (
								<ProfileSettings />
							)}
							{activeSection === "chat" && <ChatSettings />}
							{activeSection === "appearance" && <AppearanceSettings />}
							{activeSection === "keybinds" && <KeybindSettings />}
						</div>
					</ScrollArea>
				</div>
			</DialogContent>
		</Dialog>
	);
}

function SettingsSidebar({
	activeSection,
	onSelect,
}: {
	activeSection: SettingsSection | null;
	onSelect: (section: SettingsSection) => void;
}) {
	const user = useStore(authStore, (state) => state.user);
	const statusLabel = usePresenceLabel(user?.uid || "");
	const grouped = new Map<string, typeof sections>();
	for (const section of sections) {
		const existing = grouped.get(section.category) ?? [];
		existing.push(section);
		grouped.set(section.category, existing);
	}

	return (
		<div className="flex min-w-0 flex-col gap-1">
			<div className="min-w-0 overflow-hidden px-2 py-1.5">
				<SignedInHeading status={statusLabel} />
			</div>
			<Separator />
			{Array.from(grouped.entries()).map(([category, items]) => (
				<div key={category}>
					<p className="px-2 pt-3 pb-1 font-semibold text-muted-foreground text-xs uppercase tracking-wider">
						{category}
					</p>
					{items.map((section) => {
						const Icon = section.icon;
						return (
							<button
								className={cn(
									"flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
									activeSection === section.id
										? "bg-accent text-accent-foreground"
										: "text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground"
								)}
								key={section.id}
								onClick={() => onSelect(section.id)}
								type="button"
							>
								<Icon className="size-4" />
								{section.label}
							</button>
						);
					})}
				</div>
			))}
		</div>
	);
}

function ChatSettings() {
	const sendKeyMode = useStore(uiStore, (state) => state.sendKeyMode);
	const setSendKeyMode = useStore(uiStore, (state) => state.setSendKeyMode);
	const notificationSoundMode = useStore(
		uiStore,
		(state) => state.notificationSoundMode
	);
	const setNotificationSoundMode = useStore(
		uiStore,
		(state) => state.setNotificationSoundMode
	);
	const notificationSound = useStore(
		uiStore,
		(state) => state.notificationSound
	);
	const setNotificationSound = useStore(
		uiStore,
		(state) => state.setNotificationSound
	);
	const mentionNotificationSound = useStore(
		uiStore,
		(state) => state.mentionNotificationSound
	);
	const setMentionNotificationSound = useStore(
		uiStore,
		(state) => state.setMentionNotificationSound
	);

	return (
		<div className="flex flex-col gap-6">
			<div>
				<h3 className="font-semibold text-base">Send Messages</h3>
				<p className="mt-1 text-muted-foreground text-sm">
					Choose how to send messages in chat.
				</p>
			</div>
			<Separator />
			<div className="flex flex-col gap-2">
				<SendKeyOption
					active={sendKeyMode === "enter"}
					description="Shift+Enter for new line"
					keys="Enter"
					onClick={() => setSendKeyMode("enter")}
				/>
				<SendKeyOption
					active={sendKeyMode === "cmd-enter"}
					description="Enter for new line"
					keys="Cmd + Enter"
					onClick={() => setSendKeyMode("cmd-enter")}
				/>
			</div>

			<div>
				<h3 className="font-semibold text-base">Notification Sounds</h3>
				<p className="mt-1 text-muted-foreground text-sm">
					Choose when to play notification sounds.
				</p>
			</div>
			<Separator />
			<div className="flex flex-col gap-2">
				<NotificationModeOption
					active={notificationSoundMode === "all"}
					description="Play sounds for all messages and mentions"
					label="Messages & Mentions"
					onClick={() => setNotificationSoundMode("all")}
				/>
				<NotificationModeOption
					active={notificationSoundMode === "mentions-only"}
					description="Only play sounds when you are mentioned"
					label="Mentions Only"
					onClick={() => setNotificationSoundMode("mentions-only")}
				/>
				<NotificationModeOption
					active={notificationSoundMode === "off"}
					description="No notification sounds"
					label="Off"
					onClick={() => setNotificationSoundMode("off")}
				/>
			</div>

			{notificationSoundMode !== "off" && (
				<>
					{notificationSoundMode === "all" && (
						<>
							<div>
								<h3 className="font-semibold text-base">Message Sound</h3>
								<p className="mt-1 text-muted-foreground text-sm">
									Choose a sound for message notifications.
								</p>
							</div>
							<Separator />
							<div className="flex flex-col gap-2">
								<NotificationSoundOptionCard
									active={notificationSound === "bloop"}
									label="Bloop"
									onClick={() => setNotificationSound("bloop")}
									onPreview={() => playNotificationSound("bloop")}
								/>
								<NotificationSoundOptionCard
									active={notificationSound === "plink"}
									label="Plink"
									onClick={() => setNotificationSound("plink")}
									onPreview={() => playNotificationSound("plink")}
								/>
							</div>
						</>
					)}

					<div>
						<h3 className="font-semibold text-base">Mention Sound</h3>
						<p className="mt-1 text-muted-foreground text-sm">
							Choose a sound for when you are mentioned.
						</p>
					</div>
					<Separator />
					<div className="flex flex-col gap-2">
						<NotificationSoundOptionCard
							active={mentionNotificationSound === "bloop"}
							label="Bloop"
							onClick={() => setMentionNotificationSound("bloop")}
							onPreview={() => playNotificationSound("bloop")}
						/>
						<NotificationSoundOptionCard
							active={mentionNotificationSound === "plink"}
							label="Plink"
							onClick={() => setMentionNotificationSound("plink")}
							onPreview={() => playNotificationSound("plink")}
						/>
					</div>
				</>
			)}
		</div>
	);
}

function AppearanceSettings() {
	const { theme, setTheme } = useTheme();

	return (
		<div className="flex flex-col gap-6">
			<div>
				<h3 className="font-semibold text-base">Theme</h3>
				<p className="mt-1 text-muted-foreground text-sm">
					Choose how the app looks to you.
				</p>
			</div>
			<Separator />
			<div className="grid grid-cols-3 gap-3">
				<ThemeCard
					active={theme === "light"}
					icon={Sun}
					label="Light"
					onClick={() => setTheme("light")}
				/>
				<ThemeCard
					active={theme === "dark"}
					icon={Moon}
					label="Dark"
					onClick={() => setTheme("dark")}
				/>
				<ThemeCard
					active={theme === "system"}
					icon={Monitor}
					label="System"
					onClick={() => setTheme("system")}
				/>
			</div>
		</div>
	);
}

function KeybindSettings() {
	const sendKeyMode = useStore(uiStore, (state) => state.sendKeyMode);
	const keybinds = useStore(uiStore, (state) => state.keybinds);
	const setKeybind = useStore(uiStore, (state) => state.setKeybind);
	const resetKeybinds = useStore(uiStore, (state) => state.resetKeybinds);
	const [recordingAction, setRecordingAction] = useState<KeybindAction | null>(
		null
	);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (!recordingAction) return;

			event.preventDefault();
			event.stopPropagation();

			const ignoredKeys = [
				"Shift",
				"Control",
				"Alt",
				"Meta",
				"CapsLock",
				"Tab",
				"Escape",
			];
			if (ignoredKeys.includes(event.key)) return;

			if (event.key === "Escape") {
				setRecordingAction(null);
				return;
			}

			const newKeybind: Keybind = {
				key: event.key.toLowerCase(),
				metaKey: event.metaKey,
				ctrlKey: event.ctrlKey,
				shiftKey: event.shiftKey,
				altKey: event.altKey,
			};

			setKeybind(recordingAction, newKeybind);
			setRecordingAction(null);
		},
		[recordingAction, setKeybind]
	);

	useEffect(() => {
		if (!recordingAction) return;
		window.addEventListener("keydown", handleKeyDown, true);
		return () => window.removeEventListener("keydown", handleKeyDown, true);
	}, [recordingAction, handleKeyDown]);

	const editableKeybinds: KeybindAction[] = [
		"toggleSidebar",
		"bold",
		"italic",
		"strikethrough",
		"inlineCode",
		"link",
	];

	const staticKeybinds = [
		{
			action: "Send message",
			keys: sendKeyMode === "cmd-enter" ? "Cmd + Enter" : "Enter",
		},
		{
			action: "New line",
			keys: sendKeyMode === "cmd-enter" ? "Enter" : "Shift + Enter",
		},
		{ action: "Undo", keys: "Cmd + Z" },
		{ action: "Redo", keys: "Cmd + Shift + Z" },
	];

	return (
		<div className="flex flex-col gap-6">
			<div className="flex items-center justify-between">
				<div>
					<h3 className="font-semibold text-base">Keyboard Shortcuts</h3>
					<p className="mt-1 text-muted-foreground text-sm">
						Click on a shortcut to customize it.
					</p>
				</div>
				<Button
					className="gap-2"
					onClick={resetKeybinds}
					size="sm"
					variant="outline"
				>
					<RotateCcw className="size-4" />
					Reset All
				</Button>
			</div>
			<Separator />

			<div>
				<h4 className="mb-2 font-medium text-muted-foreground text-sm">
					Customizable
				</h4>
				<div className="flex flex-col gap-1">
					{editableKeybinds.map((action) => {
						const isRecording = recordingAction === action;
						const currentKeybind = keybinds[action];
						const defaultKeybind = DEFAULT_KEYBINDS[action];
						const isModified =
							JSON.stringify(currentKeybind) !== JSON.stringify(defaultKeybind);

						return (
							<button
								className={cn(
									"flex items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors",
									isRecording
										? "bg-primary/10 ring-2 ring-primary"
										: "hover:bg-muted/50"
								)}
								key={action}
								onClick={() => setRecordingAction(isRecording ? null : action)}
								type="button"
							>
								<span className="flex items-center gap-2">
									{KEYBIND_LABELS[action]}
									{isModified && (
										<span className="rounded bg-primary/20 px-1.5 py-0.5 font-medium text-primary text-xs">
											Modified
										</span>
									)}
								</span>
								<kbd
									className={cn(
										"rounded-md border px-2 py-1 font-mono text-xs",
										isRecording
											? "animate-pulse border-primary bg-primary/10 text-primary"
											: "bg-muted text-muted-foreground"
									)}
								>
									{isRecording
										? "Press keys..."
										: formatKeybind(currentKeybind)}
								</kbd>
							</button>
						);
					})}
				</div>
			</div>

			<div>
				<h4 className="mb-2 font-medium text-muted-foreground text-sm">
					Fixed Shortcuts
				</h4>
				<div className="flex flex-col gap-1">
					{staticKeybinds.map((keybind) => (
						<div
							className="flex items-center justify-between rounded-md px-3 py-2 text-sm"
							key={keybind.action}
						>
							<span className="text-muted-foreground">{keybind.action}</span>
							<kbd className="rounded-md border bg-muted px-2 py-1 font-mono text-muted-foreground text-xs">
								{keybind.keys}
							</kbd>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

function ProfileSettings() {
	const user = useStore(authStore, (state) => state.user);
	const users = useStore(coreStore, (state) => state.users);
	const currentUserProfile = user?.uid ? users.get(user.uid)?.profile : null;

	const [preferredName, setPreferredName] = useState(
		currentUserProfile?.displayName ?? ""
	);
	const [isSaving, setIsSaving] = useState(false);
	const [saveStatus, setSaveStatus] = useState<"idle" | "success" | "error">(
		"idle"
	);
	useEffect(() => {
		if (currentUserProfile) {
			setPreferredName(currentUserProfile.displayName ?? "");
		}
	}, [currentUserProfile?.displayName]);

	// Profile editing is disabled, because the profile is imported and read-only.
	const handleSave = async () => {
		if (!preferredName.trim()) return;

		setIsSaving(true);
		setSaveStatus("idle");

		try {
			await ws.sendWithPromise({
				id: ulid(),
				procedure: "users.updateProfile",
				data: { preferredName: preferredName.trim() },
			});
			setSaveStatus("success");
			// biome-ignore lint/style/noMagicNumbers: timeout duration for success message
			setTimeout(() => setSaveStatus("idle"), 2000);
		} catch (error) {
			console.error("Failed to update profile:", error);
			setSaveStatus("error");
		} finally {
			setIsSaving(false);
		}
	};

	const hasChanges =
		preferredName.trim() !== (currentUserProfile?.displayName ?? "");

	return (
		<div className="flex flex-col gap-6">
			<div>
				<p className="mt-1 text-muted-foreground text-sm">
					Manage your profile information.
				</p>
			</div>
			<Separator />
			<div className="flex flex-col gap-4">
				<div className="flex flex-col gap-2">
					<label className="font-medium text-sm" htmlFor="preferredName">
						Preferred Name
					</label>
					<p className="text-muted-foreground text-xs">
						This is how your name will appear to others.
					</p>
					{/* Profile editing is disabled, because the profile is imported and read-only. */}
					<Input
						disabled
						id="preferredName"
						maxLength={100}
						onChange={(e) => setPreferredName(e.target.value)}
						placeholder="Enter your preferred name"
						readOnly
						value={preferredName}
					/>
				</div>
				<div className="flex items-center gap-2">
					<Button disabled={true || !hasChanges || isSaving} onClick={handleSave}>
						{isSaving && <Loader2 className="mr-2 size-4 animate-spin" />}
						Save Changes
					</Button>
					{saveStatus === "success" && (
						<span className="text-green-600 text-sm">Saved successfully!</span>
					)}
					{saveStatus === "error" && (
						<span className="text-red-600 text-sm">
							Failed to save. Please try again.
						</span>
					)}
				</div>
				<Separator />
				<ProfileImageUpload />
			</div>
		</div>
	);
}

function SendKeyOption({
	active,
	keys,
	description,
	onClick,
}: {
	active: boolean;
	keys: string;
	description: string;
	onClick: () => void;
}) {
	return (
		<button
			className={cn(
				"flex items-center gap-3 rounded-lg border-2 px-4 py-3 text-left transition-colors",
				active
					? "border-primary bg-accent"
					: "border-transparent bg-muted/50 hover:bg-muted"
			)}
			onClick={onClick}
			type="button"
		>
			<div className="flex-1">
				<kbd className="rounded-md border bg-muted px-2 py-0.5 font-mono text-sm">
					{keys}
				</kbd>
				<p className="mt-1 text-muted-foreground text-xs">{description}</p>
			</div>
		</button>
	);
}

function ThemeCard({
	active,
	icon: Icon,
	label,
	onClick,
}: {
	active: boolean;
	icon: React.ElementType;
	label: string;
	onClick: () => void;
}) {
	return (
		<button
			className={cn(
				"flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors",
				active
					? "border-primary bg-accent"
					: "border-transparent bg-muted/50 hover:bg-muted"
			)}
			onClick={onClick}
			type="button"
		>
			<Icon className="size-6" />
			<span className="font-medium text-sm">{label}</span>
		</button>
	);
}

function NotificationModeOption({
	active,
	label,
	description,
	onClick,
}: {
	active: boolean;
	label: string;
	description: string;
	onClick: () => void;
}) {
	return (
		<button
			className={cn(
				"flex items-center gap-3 rounded-lg border-2 px-4 py-3 text-left transition-colors",
				active
					? "border-primary bg-accent"
					: "border-transparent bg-muted/50 hover:bg-muted"
			)}
			onClick={onClick}
			type="button"
		>
			<div className="flex-1">
				<span className="font-medium text-sm">{label}</span>
				<p className="mt-0.5 text-muted-foreground text-xs">{description}</p>
			</div>
		</button>
	);
}

function NotificationSoundOptionCard({
	active,
	label,
	onClick,
	onPreview,
}: {
	active: boolean;
	label: string;
	onClick: () => void;
	onPreview?: () => void;
}) {
	return (
		<div
			className={cn(
				"flex items-center gap-3 rounded-lg border-2 px-4 py-3 transition-colors",
				active
					? "border-primary bg-accent"
					: "border-transparent bg-muted/50 hover:bg-muted"
			)}
		>
			<button
				className="flex flex-1 items-center gap-3 text-left"
				onClick={onClick}
				type="button"
			>
				<Volume2 className="size-4" />
				<span className="font-medium text-sm">{label}</span>
			</button>
			{onPreview && (
				<Button
					className="size-8"
					onClick={(e) => {
						e.stopPropagation();
						onPreview();
					}}
					size="icon"
					variant="ghost"
				>
					<Play className="size-4" />
				</Button>
			)}
		</div>
	);
}
